// Tumble physics — timer-based hitstun duration
export const TUMBLE = {
  ENTRY_SPEED: 2.0,           // total speed threshold to enter tumble
  HITSTUN_PER_SPEED: 24,      // hitstunFrames = speed × this (frames)
  MIN_HITSTUN: 18,            // ~0.3s minimum tumble (reduced from 30 — less punishing at low %)
  MAX_HITSTUN: 180,           // ~3.0s cap
  // Bounce physics (natural, not counted)
  UNTECH_BOUNCE_RETENTION: 0.65, // velocity multiplier per unteched bounce
  MIN_BOUNCE_VY: 1.5,         // minimum upward vy after bounce (prevents dribble)
  LAUNCH_ARMOR_FRAMES: 6,     // ONE-SHOT crater escape, never reset on bounce
  AIRBORNE_FRICTION: 0.995,   // horizontal friction while airborne in tumble
  GROUNDED_FRICTION: 0.96,    // horizontal friction while sliding on ground in tumble
  CANCEL_SPEED: 0.4,          // total speed below which tumble ends early
  TECH_THRESHOLD: 1.8,        // impact speed below which tumble snaps solid instead of bouncing
  SPIN_RATE: 0.06,            // rotation per unit of vx per frame
  SPIN_VY_RATE: 0.045,        // rotation per unit of vy per frame
  // Tumble alpha pulse — accelerating pulse signals recovery timing
  PULSE_SPEED_BASE: 0.006,    // slow pulse at ragdoll start
  PULSE_SPEED_ACCEL: 0.024,   // additional speed as timer runs down
  PULSE_TIMER_REF: 120,       // reference frames for pulse scaling
  // Spin sparks — particles fly off tangentially during tumble spin
  SPIN_SPARK_MIN_RATE: 0.08,     // min rotation/frame to spawn sparks
  SPIN_SPARK_COOLDOWN_BASE: 8,   // frames between spawns at min rate
  SPIN_SPARK_COOLDOWN_FAST: 2,   // frames between spawns at max rate
  SPIN_SPARK_SPEED_MIN: 2.0,     // particle speed at min spin
  SPIN_SPARK_SPEED_MAX: 6.0,     // particle speed at max spin
  SPIN_SPARK_LIFE: 0.15,         // seconds
  SPIN_SPARK_SCALE: 0.4,         // bright spark particle scale
  SPIN_SPARK_DARK_SCALE: 1.0,    // dark framing particle scale
  SPIN_SPARK_DARK_LIFE: 0.12,    // dark framing lifetime
  // Tumble sub-states: hitstun → recovery
  HITSTUN_FRACTION: 0.65,          // 65% of total = hitstun, 35% = recovery
  RECOVERY_AIR_ACCEL: 0.08,        // px/frame² at full ramp (compare: gravity=0.06)
  RECOVERY_CONTROL_MIN: 0.20,      // 20% air control at recovery start
  RECOVERY_SPIN_DAMP: 0.97,        // spin rate multiplier during recovery (slows toward upright)
  RECOVERY_SPARK_COOLDOWN: 12,     // slower spark rate vs hitstun (2-8 frames)
  IMMUNITY_SNAP_NY: 0.85,   // |ny| for immunity branch ground snap
                             // 0.85 ≈ only near-flat. Steeper → fly past
  SLOPE_FRICTION: 0.92,     // one-time energy loss on ragdoll slope tangent projection
  IMMUNITY_VY_DAMP: 0.3,    // vy multiplier on immunity ground correction
  // Bounce VFX scaling
  BOUNCE_INTENSITY_DIVISOR: 8.0,  // speed → 0..1 intensity normalization
  SLOPE_IMPACT_SCALE: 0.3,        // slope impacts render at 30% of wall intensity
  BOUNCE_SHAKE: 6,                 // screen shake intensity multiplier for wall reflection
  BOUNCE_SHAKE_DECAY: 0.75,        // screen shake decay for wall reflection
} as const;

// Wall bounce — pinball bumper at world edges (damage-scaled reflection)
export const WALL_BOUNCE = {
  // Physics
  BASE_RETENTION: 0.7,         // vx reflection retention at 0% damage
  DAMAGE_SCALE: 100,           // vx *= (1 + damagePercent / DAMAGE_SCALE)
  MAX_BOUNCE_MULT: 3.0,        // cap on damage-scaled multiplier
  POP_VY: -2.5,                // upward pop on non-spike bounces
  SPIKE_ANGLE_THRESHOLD: 0.85, // |vy| / speed ratio above which = spike (no pop)
  SPIKE_VY_RETENTION: 0.9,     // vy retention on spike (keeps downward momentum)
  NON_SPIKE_VY_DAMPEN: 0.6,    // vy dampen on normal bounce (softens vertical)
  INTENSITY_DIVISOR: 10,        // speed → 0..1 intensity normalization for VFX

  // Visual — blade/platform effect
  BLADE_WIDTH: 60,              // px tall blade visual
  BLADE_COLOR: 0x00ffff,        // terrain edge cyan
  BLADE_DARK_COLOR: 0x050520,   // dark backing (Sakurai contrast)
  SPIKE_COLOR: 0xff4444,        // red-hot on spike bounces
  SPIKE_DARK_COLOR: 0x220000,

  // Effect layers
  FLASH_ALPHA: 0.12,
  SHAKE_INTENSITY: 8,
  SHAKE_DECAY: 0.8,
  SPIKE_SHAKE_INTENSITY: 18,
  SPIKE_SHAKE_DECAY: 0.85,
  SPIKE_FLASH_ALPHA: 0.2,
  SPARK_COUNT: 8,
  SPIKE_SPARK_COUNT: 14,
  IMPACT_LINE_COUNT: 4,
  SPIKE_IMPACT_LINE_COUNT: 6,
  SHOCKWAVE_RADIUS: 30,
  SPIKE_SHOCKWAVE_RADIUS: 50,
} as const;

// Ceiling bounce — damage-scaled ragdoll bounce off ceiling (replaces sky death)
export const CEILING_BOUNCE = {
  ENABLED: false,
  STANDARD_CEILING_Y: 0,        // top of visible world (standard/non-ascent modes)
  // Physics (mirrors WALL_BOUNCE)
  BASE_RETENTION: 0.7,
  DAMAGE_SCALE: 100,
  MAX_BOUNCE_MULT: 3.0,
  INTENSITY_DIVISOR: 10,
  // VFX
  BLADE_COLOR: 0x00ffff,        // cyan blade
  BLADE_DARK_COLOR: 0x050520,
  BLADE_WIDTH: 60,
  SHAKE_INTENSITY: 8,
  SHAKE_DECAY: 0.8,
  SPARK_COUNT: 8,
  IMPACT_LINE_COUNT: 4,
  SHOCKWAVE_RADIUS: 30,
} as const;

// Launch trail — ragdoll flight particles (speed-scaled, 4-tier density)
export const LAUNCH_TRAIL = {
  MIN_SPEED: 2.0,           // below this, no trail
  TIER_MED: 4.0,
  TIER_HIGH: 6.0,
  TIER_KO: 8.0,
  COOLDOWN_LOW: 4,          // frames between spawns
  COOLDOWN_MED: 3,
  COOLDOWN_HIGH: 2,
  COOLDOWN_KO: 1,
  // Wisps (ADD blend, soft glow)
  WISP_SCALE_MIN: 0.3,
  WISP_SCALE_MAX: 0.6,
  WISP_LIFE_MIN: 0.08,
  WISP_LIFE_MAX: 0.2,
  WISP_ALPHA: 0.7,
  // Dark smoke (NORMAL blend, soft glow)
  SMOKE_COLOR: 0x0a0a14,
  SMOKE_SCALE_MIN: 0.5,
  SMOKE_SCALE_MAX: 1.0,
  SMOKE_LIFE_MIN: 0.15,
  SMOKE_LIFE_MAX: 0.3,
  SMOKE_ALPHA: 0.5,
  // Bright streaks (ADD blend, hard glow, stretched)
  STREAK_STRETCH: 3.0,
  STREAK_SCALE: 0.3,
  STREAK_LIFE: 0.12,
  STREAK_ALPHA: 0.8,
  // Impact lines (geometric, crisp)
  LINE_EXPAND: 40,
  LINE_WIDTH: 2,
  LINE_LIFE: 0.1,
  // Afterimage glow (KO tier only)
  AFTERIMAGE_SCALE: 2.5,
  AFTERIMAGE_LIFE: 0.15,
  AFTERIMAGE_ALPHA: 0.3,
} as const;

// Launch effects — rubber-band snap stretch + anticipation compression
export const LAUNCH_FX = {
  RESOLVE_SQUASH: 0.58,        // deep compress at resolve frame (below SQUASH_MIN — 1 frame only, then spring takes over)
  RESOLVE_IMPULSE: 0.50,       // large positive = spring rockets into stretch (~1.4 cap within 2-3 frames)
  FLASH_BRIGHT: 0xffffff,      // white launch flash
  FLASH_DARK: 0x001122,        // dark teal contrast
  FLASH_DURATION: 0.25,        // seconds
  ANTICIPATION_DURATION: 0.25, // 15 frames of visible compression before snap (was 0.15)
  ANTICIPATION_TARGET: 0.65,   // deep target — 35% compression, clearly visible even on small hex body
} as const;

// Sky death sequence
export const SKY_DEATH = {
  FREEZE_MS: 400,
  ANIMATION_DURATION: 1200,
  SHAKE_INTENSITY: 60,
  FLASH_COLOR: 0xffffff,
  FLASH_ALPHA: 0.6,
  TEXT_COLOR: 0x88ddff,
} as const;

// Death sequence
export const DEATH = {
  CRATER_RADIUS: 100,
  FREEZE_MS: 500,
  ANIMATION_DURATION: 1500,
  PARTICLE_WAVES: [
    { count: 100, color: 0xffffff, delay: 0 },
    { count: 80, color: -1, delay: 0 },     // -1 = player color
    { count: 60, color: 0xff8800, delay: 0 },
  ],
  SHAKE_INTENSITY: 80,
  FLASH_ALPHA: 0.7,
} as const;
